import { Currency, Language } from '@agoda/layout';
import { cultureRegex, cultureRegexNoTrailing } from 'core-framework/localization/culture';
import { defaultLanguage, defaultLanguageCN } from 'core-framework/constant/ApplicationInfo';
import { NavigationState } from 'store/slice';
import { QueryString } from 'core/utils/queryString/queryString';
import { parseQueryString } from 'core-framework/util/CommonUtils';
import { getBootstrapConfig } from 'core/window/windowService';

import { hostname, search as getLocationSearch, pathname as getLocationPathname, hash as getLocationHash } from 'core-framework/window/location';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { SearchQueryKey } from 'core-framework/constant/SearchQueryKey';

const AGODA_CN_DOMAIN = 'agoda.cn';

export const getPathName = (state: NavigationState) => (state.location ? state.location.pathname : getLocationPathname()) || '';
export const locationSearch = (state: NavigationState) => (state.location ? state.location.search : getLocationSearch()) || '';
export const locationHash = (state: NavigationState) => (state.location ? state.location.hash : getLocationHash()) || '';

const defaultCultureCode = (config: BootstrapConfig = getBootstrapConfig()) => {
    if (config?.behavior?.common?.isDefaultLanguageFromConfig) {
        return config.whiteLabelConfig.defaultLanguage;
    }
    const hostName = hostname() || '';
    if (hostName.toLowerCase().includes(AGODA_CN_DOMAIN)) {
        return defaultLanguageCN.cultureCode;
    }
    return defaultLanguage.cultureCode;
};

export const withQueryString = (inputRoute: string, cid: string, queryParams?: Record<string, string>, ignoreParams: string[] = [], addQueryString = true): string => {
    const route = inputRoute || '';
    const pathParts = route.split('?');
    const pathPrefix = pathParts.length > 1 ? pathParts[0] : route;
    const prefix = pathPrefix.endsWith('/') ? pathPrefix.substring(0, pathPrefix.length - 1) : pathPrefix;

    if (!addQueryString) {
        return prefix;
    }

    const validatedPaths = pathParts.length > 1 ? pathParts[1] : '';
    const routeQuery = QueryString.parseQueryString(validatedPaths) || {};
    const routeQueryLowerCase = Object.keys(routeQuery).reduce<Record<string, string>>((queryIgnoreCase: Record<string, string>, param: string) => {
        queryIgnoreCase[param.toLowerCase()] = routeQuery[param];
        return queryIgnoreCase;
    }, {});

    const locationQuery = parseQueryString();
    if (locationQuery) {
        Object.keys(locationQuery).forEach((key) => {
            if (!routeQueryLowerCase[key.toLowerCase()]) {
                routeQuery[key] = locationQuery[key];
            }
        });
    }
    if (queryParams) {
        Object.keys(queryParams).forEach((key) => {
            if (queryParams[key]) {
                routeQuery[key] = queryParams[key];
            }
        });
    }

    if (cid !== '-1' && cid !== '-999' && (!routeQuery.cid || routeQuery.cid !== cid)) {
        routeQuery.cid = cid;
    }

    const ignoreParamsWithPage = [...ignoreParams, 'locale'];
    for (let i = 0; i < ignoreParamsWithPage.length; i += 1) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete routeQuery[ignoreParamsWithPage[i]];
    }

    const finalQuery = new QueryString(routeQuery);
    const finalQueryString = finalQuery.toString();
    if (finalQueryString) {
        return `${prefix}?${finalQueryString}`;
    }
    return inputRoute;
};

export const stripCultureCode = (path: string) => {
    if (cultureRegex.test(path)) {
        return path.replace(cultureRegex, '/');
    }
    if (cultureRegexNoTrailing.test(path)) {
        return path.replace(cultureRegexNoTrailing, '/');
    }
    return path;
};

export const getPathWithCultureCode = (path: string, cultureCode: string, cid: string, ignoreParams: string[] = [], addQueryString = true, config = getBootstrapConfig()) => {
    let currentPath = stripCultureCode(path);

    if (currentPath.endsWith('/')) {
        const lastSlashPos = currentPath.lastIndexOf('/');
        if (lastSlashPos > -1) {
            currentPath = currentPath.substring(0, lastSlashPos);
        }
    }

    const defaultCultureCodeStr = defaultCultureCode(config);
    const replaceCulture = cultureCode !== defaultCultureCodeStr ? `/${cultureCode}` : '';
    currentPath = `${replaceCulture}${currentPath}`;
    currentPath = withQueryString(encodeURI(currentPath), cid, undefined, ignoreParams, addQueryString);
    currentPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
    return currentPath;
};

export const getPathWithLanguage = (state: NavigationState, language: Language) => {
    let currentPath = getPathName(state);
    currentPath = stripCultureCode(currentPath);

    if (currentPath.endsWith('/')) {
        const lastSlashPos = currentPath.lastIndexOf('/');
        if (lastSlashPos > -1) {
            currentPath = currentPath.substring(0, lastSlashPos);
        }
    }

    const defaultCultureCodeStr = defaultCultureCode();
    const replaceCulture = language.cultureCode !== defaultCultureCodeStr ? `/${language.cultureCode}` : '';
    // const replaceCulture = `/${language.cultureCode}`;
    currentPath = `${replaceCulture}${currentPath}${locationSearch(state)}${locationHash(state)}`;
    currentPath = withQueryString(encodeURI(currentPath), state.cid.toString());
    currentPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
    return currentPath;
};

export const getPathWithCurrency = (state: NavigationState, currency: Currency, isRemovePriceFilter = false) => {
    let currentPath = `${getPathName(state)}${locationSearch(state)}${locationHash(state)}`;
    currentPath = withQueryString(currentPath, state.cid.toString(), { currency: currency.code }, isRemovePriceFilter ? [SearchQueryKey.PriceFrom, SearchQueryKey.PriceTo] : []);
    currentPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
    return currentPath;
};
